import React from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Typography,
  Box,
  Grid,
  Chip,
  IconButton,
  Divider,
  Paper,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import EditIcon from "@mui/icons-material/Edit";
//import CARDetailView from "./CARDetailView";
import CARPDF from "./CARPDF"; // Import the new CARPDF component
import UpdateCAR from "./UpdateCAR";
import { useState } from "react";

// Notice we're now accepting onEdit as a prop
export default function CARDetailView({
  open,
  card,
  onClose,
  onEdit = () => {},
  fetchCARData = () => {}, // Add fetchCARData prop
}) {
  console.log("CARDetailView props:", { open, card });

  const [detailOpen, setDetailOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [selectedRecord, setSelectedRecord] = useState(null);

  // When you want to show details:
  const handleShowDetail = (record) => {
    setSelectedRecord(record);
    setDetailOpen(true);
  };

  // When you want to edit:
  const handleEdit = (record) => {
    setSelectedRecord(record);
    setDetailOpen(false); // Close detail view
    setEditOpen(true); // Open update dialog
  };

  // Add a handler to close both dialogs and refresh data
  const handleUpdateClose = () => {
    setEditOpen(false);
    onClose(); // Close the detail dialog so parent can refresh and reopen with new data
    fetchCARData(); // Ensure parent data is refreshed
  };

  // Early return if no card data is available
  if (!card) return null;

  // Helper function to format dates
  const formatDate = (dateString) => {
    if (!dateString) return "Not specified";
    return dateString;
  };

  // Helper function to get status color based on content
  const getStatusColor = (status) => {
    if (!status) return "default";
    const normalized = status.toLowerCase();
    if (normalized.includes("open")) return "info";
    if (normalized.includes("closed")) return "success";
    if (normalized.includes("past")) return "warning";
    return "default";
  };

  // Helper function to display field value or fallback
  const getFieldValue = (value, fallback = "Not specified") => {
    if (value === "") return fallback;
    if (value === "false" || value === "true") {
      return value === "true" ? "Yes" : "No";
    }
    if (typeof value === "object" && value !== null) {
      return (
        value.zc_display_value ||
        value.Name ||
        value.account_name ||
        value.SO_name ||
        fallback
      );
    }
    return value || fallback;
  };

  // Helper to check if field exists and is true
  const isTrue = (field) => {
    return card[field] === "true";
  };

  // Create arrays of classifications
  const getClassifications = () => {
    const items = [];

    // Process
    if (isTrue("EstimatingSchedulingArtCreator"))
      items.push("Estimating/Scheduling/Art");
    if (isTrue("PrintingFinishingCreator")) items.push("Printing/Finishing");
    if (isTrue("PurchasingReceivingShippingCreator"))
      items.push("Purchasing/Receiving/Shipping");
    if (isTrue("OtherprocessCreator")) items.push("Other Process");

    // Source
    if (isTrue("CustomerSourceCreator")) items.push("Customer");
    if (isTrue("SupplierSourceCreator")) items.push("Supplier");
    if (isTrue("InternalAuditFindingCreator"))
      items.push("Internal Audit Finding");
    if (isTrue("ExternalAuditFindingCreator"))
      items.push("External Audit Finding");
    if (isTrue("ManagementReviewCreator")) items.push("Management Review");
    if (isTrue("Employee_Source_Creator")) items.push("Employee Source");
    if (isTrue("InstallationSourceCreator")) items.push("Installation");
    if (isTrue("OtherSourceCreator")) items.push("Other Source");

    // Priority
    if (isTrue("UrgentCriticalCreator")) items.push("Urgent/Critical Priority");
    if (isTrue("HighPriorityCreator")) items.push("High Priority");
    if (isTrue("MediumPriorityCreator")) items.push("Medium Priority");
    if (isTrue("LowPriorityCreator")) items.push("Low Priority");

    // Type
    if (isTrue("Corrective_action")) items.push("Corrective Action");
    if (isTrue("Preventive_action_potential_issue"))
      items.push("Preventive Action/Potential Issue");
    if (isTrue("Opportunity_for_Improvement_suggestion"))
      items.push("Opportunity for Improvement/Suggestion");

    // Results
    if (isTrue("PassCreator")) items.push("Pass");
    if (isTrue("FailCreator")) items.push("Fail");

    return items;
  };

  const classifications = getClassifications();

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="lg"
      scroll="paper"
    >
      <DialogTitle
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          pb: 1,
          bgcolor: "#f8f9fa",
        }}
      >
        <Box display="flex" alignItems="center">
          <Typography variant="h6" component="div" sx={{ mr: 2 }}>
            {card.carNumber || card.CAR_number || "CAR Detail View"}
          </Typography>
        </Box>
        <Box>
          <CARPDF card={card} />
          <IconButton
            aria-label="edit"
            sx={{ mr: 1, color: "#4F46E5" }}
            onClick={() => handleEdit(card)}
          >
            <EditIcon />
          </IconButton>
          <IconButton
            edge="end"
            color="inherit"
            onClick={onClose}
            aria-label="close"
          >
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      <Divider />
      <UpdateCAR
        open={editOpen}
        record={selectedRecord}
        onClose={handleUpdateClose}
        fetchCARData={fetchCARData}
      />
      <DialogContent sx={{ p: 3 }}>
        <Box mb={4}>
          <Typography
            variant="h6"
            gutterBottom
            sx={{ fontWeight: "bold", color: "#4F46E5" }}
          >
            CAR Name:{" "}
            {card.name || card.title || card.CAR_Name || "Unnamed CAR"}
          </Typography>
          <Paper elevation={0} sx={{ p: 3, bgcolor: "#f8f9fa", mb: 3 }}>
            <Grid container spacing={3}>
              <Grid item xs={12} sm={6} md={4}>
                <Typography variant="subtitle2" color="text.secondary">
                  Created Date
                </Typography>
                <Typography variant="body1" fontWeight="medium">
                  {formatDate(card.date || card.CAR_Date)}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <Typography variant="subtitle2" color="text.secondary">
                  Customer
                </Typography>
                <Typography variant="body1" fontWeight="medium">
                  {getFieldValue(card.CRM_Account_Name_CAR)}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <Typography variant="subtitle2" color="text.secondary">
                  Job Name
                </Typography>
                <Typography variant="body1" fontWeight="medium">
                  {getFieldValue(card.Job_name)}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <Typography variant="subtitle2" color="text.secondary">
                  Machine
                </Typography>
                <Typography variant="body1" fontWeight="medium">
                  {getFieldValue(card.Machine)}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <Typography variant="subtitle2" color="text.secondary">
                  Employee
                </Typography>
                <Typography variant="body1" fontWeight="medium">
                  {getFieldValue(card.Employee)}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <Typography variant="subtitle2" color="text.secondary">
                  CAR Reported By
                </Typography>
                <Typography variant="body1" fontWeight="medium">
                  {getFieldValue(card.CAR_Reported_By)}
                </Typography>
              </Grid>
            </Grid>
          </Paper>
        </Box>
        {/* Classification Section */}
        <Box mb={4}>
          <Typography
            variant="h6"
            gutterBottom
            sx={{ fontWeight: "bold", color: "#4F46E5" }}
          >
            Checkboxes Details
          </Typography>
          <Paper elevation={0} sx={{ p: 3, mb: 3 }}>
            <Grid container spacing={2}>
              {classifications.map((item, index) => (
                <Grid item key={index}>
                  <Chip
                    label={item}
                    color="#000000"
                    //sx={{ bgcolor: "#4F46E5" }}
                  />
                </Grid>
              ))}
              {classifications.length === 0 && (
                <Grid item xs={12}>
                  <Typography variant="body2" color="text.secondary">
                    No classifications specified
                  </Typography>
                </Grid>
              )}
            </Grid>
          </Paper>
        </Box>

        {/* Problem Description */}
        <Box mb={4}>
          <Typography
            variant="h6"
            gutterBottom
            sx={{ fontWeight: "bold", color: "#4F46E5" }}
          >
            Problem Description
          </Typography>
          <Paper elevation={0} sx={{ p: 3, bgcolor: "#f8f9fa", mb: 3 }}>
            <Typography variant="body1">
              {getFieldValue(
                card.Describe_the_issue_or_problem_in_detail,
                "No description provided"
              )}
            </Typography>
          </Paper>
        </Box>

        {/* Assignment Information */}
        <Box mb={4}>
          <Typography
            variant="h6"
            gutterBottom
            sx={{ fontWeight: "bold", color: "#4F46E5" }}
          >
            Assignment Information
          </Typography>
          <Paper elevation={0} sx={{ p: 3, bgcolor: "#f8f9fa", mb: 3 }}>
            <Grid container spacing={3}>
              <Grid item xs={12} sm={6} md={4}>
                <Typography variant="subtitle2" color="text.secondary">
                  Assigned To
                </Typography>
                <Typography variant="body1" fontWeight="medium">
                  {getFieldValue(card.CAR_PAR_Assigned_to || card.assignedTo)}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <Typography variant="subtitle2" color="text.secondary">
                  Date Assigned
                </Typography>
                <Typography variant="body1" fontWeight="medium">
                  {formatDate(card.Date_Assigned)}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <Typography variant="subtitle2" color="text.secondary">
                  Respond By
                </Typography>
                <Typography variant="body1" fontWeight="medium">
                  {formatDate(card.Respond_by || card.respondBy)}
                </Typography>
              </Grid>
            </Grid>
          </Paper>
        </Box>

        {/* Assignee Response */}
        <Box mb={4}>
          <Typography
            variant="h6"
            gutterBottom
            sx={{ fontWeight: "bold", color: "#4F46E5" }}
          >
            Assignee Response
          </Typography>
          <Paper elevation={0} sx={{ p: 3, bgcolor: "#f8f9fa", mb: 3 }}>
            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
              Containment / Immediate Corrections
            </Typography>
            <Typography variant="body1" paragraph>
              {getFieldValue(
                card.Containment_Immediate_Corrections,
                "No containment actions recorded"
              )}
            </Typography>

            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
              Root Cause of Problem
            </Typography>
            <Typography variant="body1" paragraph>
              {getFieldValue(
                card.Root_cause_of_Problem,
                "No root cause analysis provided"
              )}
            </Typography>

            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
              Action Plan
            </Typography>
            <Typography variant="body1" paragraph>
              {getFieldValue(card.Action_Plan, "No action plan recorded")}
            </Typography>

            <Grid container spacing={3} mt={2}>
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" color="text.secondary">
                  Date Action Completed
                </Typography>
                <Typography variant="body1" fontWeight="medium">
                  {formatDate(card.Date_Action_Completed)}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" color="text.secondary">
                  Assignee Name
                </Typography>
                <Typography variant="body1" fontWeight="medium">
                  {getFieldValue(card.Assignee_Name)}
                </Typography>
              </Grid>
            </Grid>
          </Paper>
        </Box>

        {/* Verification */}
        <Box mb={4}>
          <Typography
            variant="h6"
            gutterBottom
            sx={{ fontWeight: "bold", color: "#4F46E5" }}
          >
            Verification
          </Typography>
          <Paper elevation={0} sx={{ p: 3, bgcolor: "#f8f9fa", mb: 3 }}>
            <Grid container spacing={3}>
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" color="text.secondary">
                  Verified By
                </Typography>
                <Typography variant="body1" fontWeight="medium">
                  {getFieldValue(card.Verified_by)}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" color="text.secondary">
                  Verified Date
                </Typography>
                <Typography variant="body1" fontWeight="medium">
                  {formatDate(card.Verified_Date)}
                </Typography>
              </Grid>
              <Grid item xs={12}>
                <Typography variant="subtitle2" color="text.secondary">
                  Result
                </Typography>
                <Typography variant="body1" fontWeight="medium">
                  {isTrue("PassCreator")
                    ? "Pass"
                    : isTrue("FailCreator")
                    ? "Fail"
                    : "Not verified"}
                </Typography>
              </Grid>
            </Grid>
          </Paper>
        </Box>

        {/* Notes */}
        <Box mb={2}>
          <Typography
            variant="h6"
            gutterBottom
            sx={{ fontWeight: "bold", color: "#4F46E5" }}
          >
            Notes / Comments
          </Typography>
          <Paper elevation={0} sx={{ p: 3, bgcolor: "#f8f9fa" }}>
            <Typography variant="body1">
              {getFieldValue(card.Notes, "No additional notes")}
            </Typography>
          </Paper>
        </Box>
      </DialogContent>
    </Dialog>
  );
}
